import {
  useCallback,
  useMemo,
  FocusEvent,
  FC,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import TextInput from '__components/Inputs/TextInput';
import { useFlashTransition } from 'hooks/useFlashState';
import { usePreserveCaretPosition } from '__hooks/usePreserveCaretPosition';

const MAX_VARIABLE_NAME_LENGTH = 100;
const MIN_VARIABLE_NAME_LENGTH = 2;

export const VariableNameInput: FC<{
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  error: string | null;
  showError?: boolean;
  setError: (error: string | null) => void;
  uniqueNames: string[];
  margin?: boolean | string;
}> = ({
  disabled,
  error,
  showError,
  setError,
  value,
  onChange,
  uniqueNames,
  margin,
}) => {
  const { t } = useTranslation();
  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const ref = useRef<HTMLDivElement>(null);

  const [name, setName] = useState<string>(value || '');

  useEffect(() => {
    if (error && showError) {
      flashErrorMessage(error);
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      cancelFlash();
    }
  }, [error, showError, flashErrorMessage, cancelFlash]);

  const handleNameBlur = useCallback(
    ({ target: { value } }: FocusEvent<HTMLInputElement>) => {
      const trimmedValue = value.trim();
      onChange(trimmedValue);
      if (uniqueNames.includes(trimmedValue)) {
        setError(t('Variable name must be unique.'));
      } else if (!trimmedValue) {
        setError(t('Variable name may not be blank.'));
      } else if (trimmedValue.length > MAX_VARIABLE_NAME_LENGTH) {
        setError(
          t('Maximum field size is {{maxLength}} characters.', {
            maxLength: MAX_VARIABLE_NAME_LENGTH,
          })
        );
      } else if (trimmedValue.length < MIN_VARIABLE_NAME_LENGTH) {
        setError(
          t('Minimum field size is {{minLength}} characters.', {
            minLength: MIN_VARIABLE_NAME_LENGTH,
          })
        );
      } else if (!/^[a-z_][a-z0-9_]+$/.test(trimmedValue)) {
        setError(
          t(
            'Must start with a letter or underscore and contain only lowercase letters, numbers, or underscores.'
          )
        );
      }
    },
    [onChange, t, uniqueNames, setError]
  );

  const handleNameChange = useCallback(
    (value: string) => {
      setError(null);
      const croppedName = value
        .substring(0, MAX_VARIABLE_NAME_LENGTH)
        .toLowerCase()
        .replaceAll(' ', '_');

      setName(croppedName);
    },
    [setError]
  );

  const validateVariableName = useMemo(
    () => ({ showMessage, message }),
    [showMessage, message]
  );

  const inputRef = usePreserveCaretPosition(name);

  return (
    <TextInput
      ref={ref}
      inputRef={inputRef}
      label={t('Variable Name')}
      labelInfo={t('Only letters, numbers, and _ are allowed.')}
      labelInfoPlacement="top"
      placeholder={t('Enter Variable Name')}
      value={name}
      onChange={handleNameChange}
      onBlur={handleNameBlur}
      validate={validateVariableName}
      disabled={disabled}
      error={!!error}
      required
      margin={margin}
    />
  );
};
